import { FC, useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { COLORS } from '@/shared/constants/colors';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { OwnershipType } from '@/shared/lib/sj-orm/models/asset.dto';
import { Icon } from '@/shared/uikit/Icon';

import { DiagnosticStatus } from './DiagnosticCard/DiagnosticCounter';
import { InterwillDiagnosticCard } from './DiagnosticCard';

const getStatus = (assetsCount: number) => {
  if (assetsCount === 0) {
    return DiagnosticStatus.NOT_READY;
  }
  if (assetsCount < 3) {
    return DiagnosticStatus.HALF_READY;
  }
  return DiagnosticStatus.READY;
};

type InterwillAssetDiagnosticCardProps = {
  onCreatePress: () => void;
};

export const InterwillAssetDiagnosticCard: FC<InterwillAssetDiagnosticCardProps> = ({
  onCreatePress,
}) => {
  const { t } = useTranslation('pages/pageInterwill', { keyPrefix: 'diagnosticCards.assets' });
  const { collections } = useSJDatabase();

  const assetsCount = useMemo(
    () =>
      collections.assets?.findMany(
        (asset) => asset.ownerShipType === OwnershipType.DIRECT || !asset.ownerShipType,
      ).length ?? 0,
    [collections.assets],
  );

  const status = getStatus(assetsCount);

  return (
    <InterwillDiagnosticCard
      buttonText={t('buttonText')}
      content={<Content status={status} />}
      count={assetsCount}
      iconName="briefcase"
      status={status}
      title={t('title')}
      onBtnClick={onCreatePress}
    />
  );
};

const Content: FC<{ status: DiagnosticStatus }> = ({ status }) => {
  const { t } = useTranslation('pages/pageInterwill', {
    keyPrefix: 'diagnosticCards.assets.content',
  });

  const getText = () => {
    if (status === DiagnosticStatus.NOT_READY) {
      return t('primaryText.notReady');
    }
    if (status === DiagnosticStatus.HALF_READY) {
      return t('primaryText.halfReady');
    }
    if (status === DiagnosticStatus.READY) {
      return t('primaryText.ready');
    }

    return '';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <span>{getText()}</span>
      <div style={{ display: 'flex', gap: 4, color: COLORS.blueColor }}>
        <Icon name="info" style={{ color: COLORS.blueColor }} />
        <span>{t('secondaryText')}</span>
      </div>
    </div>
  );
};
